import { useState, useMemo, useEffect, useCallback, useRef, useContext } from 'react';
import { fetchData, fetchUrl, HTTP_STATUS, intl, LanguageBaseContext } from 'dmsbasereact';
import DmsErrorSwal from '../DmsErrorSwal/DmsErrorSwal';
import { ReturnStatusEnum } from '../../constants/ReturnStatus';
import { RETURN_REASON_KEYS } from '../../constants/ReturnReasons';
import { InstitutionStatusEnum } from '../../constants/InstitutionStatus';

export const useReturnForm = ({ row, table, onHide, show, managementMode = false }) => {
    const { language } = useContext(LanguageBaseContext);
    const [data, setData] = useState({
        dispensing_pk: '',
        batch_pk: '',
        requesting_organization_pk: '',
        return_reason_pk: '',
        reason_details: '',
        reason_details_text: '',
        quantity: '',
        status: ReturnStatusEnum.PENDING,
        invoice_number: '',
        sap_tracking_code: ''
    });
    const [selectedReason, setSelectedReason] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [checkSubmitted, setCheckSubmitted] = useState(false);
    const [files, setFiles] = useState({});
    const [existingFiles, setExistingFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [batches, setBatches] = useState([]);
    const [organizationBranches, setOrganizationBranches] = useState([]);
    const [returnReasons, setReturnReasons] = useState([]);
    const [dispensings] = useState([]);
    const [searchedDispensings, setSearchedDispensings] = useState([]);
    const [checkoutData, setCheckoutData] = useState({
        batchPk: '',
        quantity: '',
        invoiceNumber: '',
        requesting_organization_pk: '',
        destinationEntityPk: ''
    });
    const [lastReturnReason, setLastReturnReason] = useState('');
    const [userProfessionalTypes, setUserProfessionalTypes] = useState(null);
    const latestInstitutionRef = useRef(null);

    const clearAllFields = useCallback(() => {
        setData(prev => ({
            ...prev,
            batch_pk: '',
            dispensing_pk: '',
            reason_details: '',
            reason_details_text: '',
            quantity: '',
            invoice_number: '',
            sap_tracking_code: ''
        }));
        setFiles({});
        setCheckSubmitted(false);
        setCheckoutData({
            batchPk: '',
            quantity: '',
            invoiceNumber: '',
            requesting_organization_pk: ''
        });
        setSearchedDispensings([]);
    }, []);

    const loadSelectData = useCallback(async () => {
        setIsLoading(true);
        try {
            const fetches = [
                fetchData('/return_reason/all', 'GET', true),
            ];

            if (!managementMode) {
                fetches.push(fetchData('/institution/user-linked', 'GET', true));
                fetches.push(fetchData('/person/my_types', 'GET', true, null, true));
            }

            const results = await Promise.all(fetches);
            const [returnReasonsRes] = results;
            const branchesResponse = managementMode ? null : results[1];
            const myTypesResponse = managementMode ? null : results[2];

            if (returnReasonsRes?.data?.data) setReturnReasons(returnReasonsRes.data.data);
            if (branchesResponse?.data?.data) {
                const branches = branchesResponse.data.data.map(inst => ({
                    pk: inst.organization_branch_pk,
                    name: inst.name,
                    is_approved: inst.is_approved,
                    status: inst.status,
                    category: inst.category
                }));
                setOrganizationBranches(branches);
            }
            if (myTypesResponse?.data?.data?.types) {
                const types = myTypesResponse.data.data.types.map(t => t.type);
                setUserProfessionalTypes(types);
            } else {
                setUserProfessionalTypes([]);
            }
        } catch (error) {
            console.error('Error loading select data:', error);
        } finally {
            setIsLoading(false);
        }
    }, [managementMode]);

    const handleInstitutionChange = useCallback(async (value) => {
        // Track the latest institution to prevent race conditions
        latestInstitutionRef.current = value;

        // Clear return reason when institution changes
        setSelectedReason(null);

        // Update the requesting organization and clear batch + reason
        setData(prev => ({
            ...prev,
            requesting_organization_pk: value,
            batch_pk: '',
            return_reason_pk: '',
            reason_details: '',
            reason_details_text: ''
        }));
        setBatches([]);

        // Fetch batches for the selected institution
        if (value) {
            try {
                const batchesRes = await fetchData(`/batch_location/institution/${value}`, 'GET', true);
                // Only update if this is still the latest selected institution
                if (latestInstitutionRef.current === value && batchesRes?.data) {
                    const batchesData = (Array.isArray(batchesRes.data) ? batchesRes.data : []).map(item => ({
                        pk: item.batch_pk,
                        number: item.batch_number,
                        part_name: item.part_name,
                        quantity: item.quantity,
                        fabrication: item.fabrication
                    }));
                    setBatches(batchesData);
                }
            } catch (error) {
                console.error('Error loading batches for institution:', error);
                if (latestInstitutionRef.current === value) {
                    setBatches([]);
                }
            }
        }
    }, [organizationBranches]);

    const loadReturnData = useCallback(async () => {
        if (!row?.pk) return;

        setIsLoading(true);
        try {
            const response = await fetchData(`/return/${row.pk}/complete`, 'GET', true);
            const returnData = response.data?.data || response.data;

            if (returnData) {
                // Convert numeric return_reason_pk to name key for consistent option matching
                const reason = returnReasons.find(r => r.pk === returnData.return_reason_pk);
                const reasonNameKey = reason?.name || returnData.return_reason_pk || '';

                setData({
                    dispensing_pk: returnData.dispensing_pk || '',
                    batch_pk: returnData.batch_pk || '',
                    requesting_organization_pk: returnData.requesting_organization_pk || '',
                    return_reason_pk: reasonNameKey,
                    reason_details: returnData.reason_details || '',
                    reason_details_text: returnData.reason_details_text || '',
                    quantity: returnData.quantity || '',
                    status: returnData.status || ReturnStatusEnum.PENDING,
                    invoice_number: returnData.invoice_number || '',
                    sap_tracking_code: returnData.sap_tracking_code || ''
                });

                setExistingFiles(returnData.files || []);
                setLastReturnReason(reasonNameKey);

                if (returnData.dispensing) {
                    const disp = returnData.dispensing;
                    setSearchedDispensings([
                        {
                            pk: returnData.dispensing_pk,
                            patient_name: returnData.patient_name,
                            prescription_identifier: returnData.prescription_identifier,
                            capsule_count: disp.capsule_count,
                            box_count: disp.box_count,
                            batch_number: disp.batch_number
                        }
                    ]);
                }
                if (reason?.name === RETURN_REASON_KEYS.ORDER_DIVERGENCE) {
                    setCheckoutData({
                        batchPk: returnData.batch_pk || '',
                        quantity: returnData.quantity || '',
                        invoiceNumber: returnData.invoice_number || '',
                        requesting_organization_pk: returnData.requesting_organization_pk || ''
                    });
                    setCheckSubmitted(true);
                }

                // For management users, populate institution from the return record
                if (managementMode && returnData.requesting_organization_pk) {
                    setOrganizationBranches([{
                        pk: returnData.requesting_organization_pk,
                        name: returnData.requesting_organization?.name || '',
                    }]);
                }

                // Load batches for the existing institution
                if (returnData.requesting_organization_pk) {
                    try {
                        const batchesRes = await fetchData(`/batch_location/institution/${returnData.requesting_organization_pk}`, 'GET', true);
                        if (batchesRes?.data) {
                            const batchesData = (Array.isArray(batchesRes.data) ? batchesRes.data : []).map(item => ({
                                pk: item.batch_pk,
                                number: item.batch_number,
                                part_name: item.part_name,
                                quantity: item.quantity,
                                fabrication: item.fabrication
                            }));
                            setBatches(batchesData);
                        }
                    } catch (error) {
                        console.error('Error loading batches for institution:', error);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading return data:', error);
            DmsErrorSwal({
                errorMessage: intl.get('ErrorLoadingReturn').d('Error loading return data'),
                onClose: () => { }
            });
        } finally {
            setIsLoading(false);
        }
    }, [row, returnReasons, managementMode]);

    useEffect(() => {
        if (show) {
            loadSelectData();
            if (row) {
                setCheckSubmitted(false);
                setSubmitted(false);
            }
        }
    }, [show, row, loadSelectData]);

    useEffect(() => {
        if (show && row && returnReasons.length > 0) {
            loadReturnData();
        }
    }, [show, row, returnReasons, loadReturnData]);

    // Update selected reason when data.return_reason_pk changes
    useEffect(() => {
        if (data.return_reason_pk) {
            // Find by name key (option values are reason.name strings)
            let reason = returnReasons.find(r => r.name === data.return_reason_pk);
            if (!reason && typeof data.return_reason_pk === 'string') {
                reason = { name: data.return_reason_pk, pk: data.return_reason_pk };
            }
            if (reason) {
                setSelectedReason(reason);
            }
        }
    }, [data.return_reason_pk, returnReasons]);

    const handleChange = useCallback((eventOrField, value) => {
        // Handle both event object format and direct (field, value) format
        if (eventOrField?.target) {
            // Event object format from DmsTextField: { target: { name, value } }
            const { name, value: targetValue } = eventOrField.target;
            setData(prev => ({
                ...prev,
                [name]: targetValue
            }));
        } else {
            // Direct format: (field, value)
            setData(prev => ({
                ...prev,
                [eventOrField]: value
            }));
        }
    }, []);

    const handleReturnReasonChange = useCallback((value) => {
        console.log('handleReturnReasonChange called with value:', value);
        console.log('Available returnReasons:', returnReasons);

        // Find the reason by name key (option values are reason.name strings)
        let reason = returnReasons.find(r => r.name === value);
        console.log('Found reason:', reason);

        if (!reason) {
            reason = { name: value, pk: value };
            console.log('Created fallback reason:', reason);
        }

        setSelectedReason(reason);
        console.log('Set selectedReason to:', reason);

        setData(prev => ({
            ...prev,
            return_reason_pk: value,
            reason_details: '',
            reason_details_text: ''
        }));

        // Clear fields when reason changes
        if (lastReturnReason !== value) {
            clearAllFields();
            setData(prev => ({
                ...prev,
                return_reason_pk: value
            }));
        }
    }, [returnReasons, lastReturnReason, clearAllFields]);

    const handleFileSelect = useCallback((fieldName, selectedFiles) => {
        setFiles(prev => ({
            ...prev,
            [fieldName]: selectedFiles
        }));
    }, []);

    const handleCheckoutDataChange = useCallback((fieldOrData, value) => {
        // Handle both object format (from CheckinCheckoutForm) and (field, value) format
        if (typeof fieldOrData === 'object' && fieldOrData !== null) {
            // Object format: { products, invoiceNumber, ownerEntityPk, destinationEntityPk }
            const data = fieldOrData;
            const firstProduct = data.products?.[0] || {};
            setCheckoutData(prev => ({
                ...prev,
                batchPk: firstProduct.batchPk || prev.batchPk,
                quantity: firstProduct.quantity || prev.quantity,
                invoiceNumber: data.invoiceNumber || prev.invoiceNumber,
                requesting_organization_pk: data.ownerEntityPk || prev.requesting_organization_pk,
                destinationEntityPk: data.destinationEntityPk || prev.destinationEntityPk
            }));
        } else {
            // Direct (field, value) format
            setCheckoutData(prev => ({
                ...prev,
                [fieldOrData]: value
            }));
        }
    }, []);

    const handleCheckoutSubmitted = useCallback(() => {
        // Transfer checkout data to main data
        setData(prev => ({
            ...prev,
            batch_pk: checkoutData.batchPk,
            quantity: checkoutData.quantity,
            invoice_number: checkoutData.invoiceNumber,
            requesting_organization_pk: checkoutData.requesting_organization_pk
        }));
        setCheckSubmitted(true);
    }, [checkoutData]);

    const fieldHasError = useCallback((fieldName) => {
        if (!submitted) return false;

        // Get the reason name for conditional validation
        let reasonName = data.return_reason_pk;
        if (typeof data.return_reason_pk === 'number' || !isNaN(Number(data.return_reason_pk))) {
            const reason = returnReasons.find(r => r.pk === Number(data.return_reason_pk));
            if (reason) {
                reasonName = reason.name;
            }
        }

        // Define required fields per reason type
        const baseDiscontinuation = ['return_reason_pk', 'dispensing_pk', 'batch_pk', 'quantity', 'reason_details'];
        const requiredFields = {
            [RETURN_REASON_KEYS.TREATMENT_DISCONTINUATION]: data.reason_details === 'OTHER'
                ? [...baseDiscontinuation, 'reason_details_text']
                : baseDiscontinuation,
            [RETURN_REASON_KEYS.QUALITY_ISSUE]: ['return_reason_pk', 'batch_pk', 'quantity', 'reason_details'],
            [RETURN_REASON_KEYS.ORDER_DIVERGENCE]: ['return_reason_pk', 'checkout_batchPk', 'checkout_quantity', 'checkout_invoiceNumber', 'checkout_ownerEntityPk', 'checkout_destinationEntityPk'],
            default: ['return_reason_pk']
        };

        const fieldsToValidate = requiredFields[reasonName] || requiredFields.default;

        // Only validate if this field is required for the current reason
        if (!fieldsToValidate.includes(fieldName)) {
            return false;
        }

        // For checkout fields (ORDER_DIVERGENCE), check checkoutData
        if (fieldName.startsWith('checkout_')) {
            let checkoutFieldName = fieldName.replace('checkout_', '');
            // Map UI field names to checkoutData field names
            const fieldMapping = {
                'ownerEntityPk': 'requesting_organization_pk'
            };
            checkoutFieldName = fieldMapping[checkoutFieldName] || checkoutFieldName;
            const value = checkoutData[checkoutFieldName];
            return value === '' || value === null || value === undefined;
        }

        const value = data[fieldName];
        return value === '' || value === null || value === undefined;
    }, [submitted, data, returnReasons, checkoutData]);

    const helperText = useCallback((fieldName) => {
        if (fieldHasError(fieldName)) {
            return intl.get('RequiredField').d('This field is required');
        }
        return '';
    }, [fieldHasError]);

    const formIsValid = useMemo(() => {
        if (!data.return_reason_pk) return false;

        // Get the reason name - could be the key directly or from the backend reason object
        let reasonName = data.return_reason_pk;

        // If it's a numeric pk, try to find the reason from backend data
        if (typeof data.return_reason_pk === 'number' || !isNaN(Number(data.return_reason_pk))) {
            const reason = returnReasons.find(r => r.pk === Number(data.return_reason_pk));
            if (reason) {
                reasonName = reason.name;
            }
        }

        // For ORDER_DIVERGENCE, validate checkoutData fields (batch, quantity, invoiceNumber)
        if (reasonName === RETURN_REASON_KEYS.ORDER_DIVERGENCE) {
            return !!checkoutData.batchPk && !!checkoutData.quantity && !!checkoutData.invoiceNumber;
        }

        // For TREATMENT_DISCONTINUATION, need dispensing, batch, quantity, and text if OTHER
        if (reasonName === RETURN_REASON_KEYS.TREATMENT_DISCONTINUATION) {
            const baseValid = !!data.dispensing_pk && !!data.batch_pk && !!data.quantity;
            if (data.reason_details === 'OTHER') {
                return baseValid && !!data.reason_details_text;
            }
            return baseValid;
        }

        // For QUALITY_ISSUE, need batch and quantity
        if (reasonName === RETURN_REASON_KEYS.QUALITY_ISSUE) {
            return !!data.batch_pk && !!data.quantity;
        }

        // For other reasons, just need the reason selected
        return true;
    }, [data, returnReasons, checkoutData]);

    const uploadFiles = useCallback(async () => {
        const uploadedFiles = [];
        for (const [docName, file] of Object.entries(files)) {
            if (file) {
                const response = await fetchUrl(
                    '/return/file_upload',
                    'POST',
                    true,
                    file,
                    false,
                    false,
                    null,
                    null,
                    'multipart/form-data'
                );
                if (response.status === HTTP_STATUS.CREATED_201 && response.data?.data?.pk) {
                    uploadedFiles.push({
                        file_pk: response.data.data.pk,
                        document_name: docName
                    });
                }
            }
        }
        return uploadedFiles;
    }, [files]);

    const handleSave = useCallback(async () => {
        setSubmitted(true);

        if (!formIsValid) {
            return;
        }

        setIsLoading(true);
        try {
            // Upload files first
            const uploadedFiles = await uploadFiles();

            // Prepare the data object with proper types
            const preparedData = {};

            // Get the reason name for conditional logic
            let reasonName = data.return_reason_pk;
            if (typeof data.return_reason_pk === 'number' || !isNaN(Number(data.return_reason_pk))) {
                const reason = returnReasons.find(r => r.pk === Number(data.return_reason_pk));
                if (reason) {
                    reasonName = reason.name;
                }
            }

            // Only include non-empty values and convert to proper types
            if (data.dispensing_pk) {
                preparedData.dispensing_pk = parseInt(data.dispensing_pk);
            }

            // For ORDER_DIVERGENCE, get batch_pk from checkoutData
            if (reasonName === RETURN_REASON_KEYS.ORDER_DIVERGENCE) {
                if (checkoutData.batchPk) {
                    preparedData.batch_pk = parseInt(checkoutData.batchPk);
                }
            } else if (data.batch_pk) {
                preparedData.batch_pk = parseInt(data.batch_pk);
            }

            if (data.requesting_organization_pk) {
                preparedData.requesting_organization_pk = parseInt(data.requesting_organization_pk);
            }
            if (data.return_reason_pk) {
                // Ensure return_reason_pk is a number (backend PK)
                const numericPk = parseInt(data.return_reason_pk);
                if (!isNaN(numericPk)) {
                    preparedData.return_reason_pk = numericPk;
                } else {
                    // If it's a string key, find the actual PK from returnReasons
                    const reason = returnReasons.find(r => r.name === data.return_reason_pk);
                    if (reason) {
                        preparedData.return_reason_pk = reason.pk;
                    } else {
                        console.error('Could not find return reason PK for:', data.return_reason_pk);
                        throw new Error('Invalid return reason');
                    }
                }
            }

            // For ORDER_DIVERGENCE, get quantity from checkoutData
            if (reasonName === RETURN_REASON_KEYS.ORDER_DIVERGENCE) {
                if (checkoutData.quantity) {
                    preparedData.quantity = parseInt(checkoutData.quantity);
                }
            } else if (data.quantity) {
                preparedData.quantity = parseInt(data.quantity);
            }

            if (data.status !== undefined && data.status !== null && data.status !== '') {
                preparedData.status = parseInt(data.status);
            }
            if (data.reason_details) {
                preparedData.reason_details = data.reason_details;
            }
            if (data.reason_details_text) {
                preparedData.reason_details_text = data.reason_details_text;
            }

            // For ORDER_DIVERGENCE, get invoice_number from checkoutData
            if (reasonName === RETURN_REASON_KEYS.ORDER_DIVERGENCE) {
                if (checkoutData.invoiceNumber) {
                    preparedData.invoice_number = checkoutData.invoiceNumber;
                }
            } else if (data.invoice_number) {
                preparedData.invoice_number = data.invoice_number;
            }

            if (data.sap_tracking_code) {
                preparedData.sap_tracking_code = data.sap_tracking_code;
            }

            // Include uploaded files
            if (uploadedFiles.length > 0) {
                preparedData.files = uploadedFiles;
            }

            const url = row?.pk ? `/return/${row.pk}` : '/return';
            const method = row?.pk ? 'PUT' : 'POST';

            // Include pk in body for PUT requests (required by GenericRouter)
            if (row?.pk) {
                preparedData.pk = row.pk;
            }

            const response = await fetchData(url, method, true, preparedData);

            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                if (table?.current?.updateTable) {
                    table.current.updateTable();
                }
                onHide();

                // Reset form on success
                setData({
                    dispensing_pk: '',
                    batch_pk: '',
                    requesting_organization_pk: '',
                    return_reason_pk: '',
                    reason_details: '',
                    reason_details_text: '',
                    quantity: '',
                    status: ReturnStatusEnum.PENDING,
                    invoice_number: '',
                    sap_tracking_code: ''
                });
                setFiles({});
                setSubmitted(false);
                setCheckSubmitted(false);
            } else {
                const detail = response.data?.detail;
                let errorMessage;
                if (typeof detail === 'string') {
                    errorMessage = detail;
                } else if (detail?.error) {
                    errorMessage = detail.error;
                } else if (detail?.message) {
                    errorMessage = detail.message;
                } else if (detail) {
                    errorMessage = JSON.stringify(detail);
                } else {
                    errorMessage = intl.get('ErrorSavingReturn').d('Error saving return');
                }
                DmsErrorSwal({
                    errorMessage,
                    onClose: () => { }
                });
            }
        } catch (error) {
            console.error('Error saving return:', error);
            const detail = error.response?.data?.detail || error.message;
            let errorMessage;
            if (typeof detail === 'string') {
                errorMessage = detail;
            } else if (detail?.error) {
                errorMessage = detail.error;
            } else if (detail?.message) {
                errorMessage = detail.message;
            } else if (detail) {
                errorMessage = JSON.stringify(detail);
            } else {
                errorMessage = intl.get('ErrorSavingReturn').d('Error saving return');
            }
            DmsErrorSwal({
                errorMessage,
                onClose: () => { }
            });
        } finally {
            setIsLoading(false);
        }
    }, [data, row, table, onHide, formIsValid, returnReasons, checkoutData, uploadFiles]);

    const institutionOptions = useMemo(() =>
        organizationBranches.map(b => {
            let displayLabel = b.name;
            const isInactive = b.status === InstitutionStatusEnum.INACTIVE;
            const isApproved = b.status === InstitutionStatusEnum.APPROVED;

            if (isInactive) {
                displayLabel = `${b.name} - ${intl.get("InactiveInstitution").d("Instituição Inativa")}`;
            } else if (!isApproved) {
                displayLabel = `${b.name} - ${intl.get("InstitutionPendingApproval").d("Aguardando aprovação da instituição")}`;
            }

            return { value: b.pk, label: displayLabel };
        }).sort((a, b) => a.label.localeCompare(b.label)),
        [organizationBranches, language]
    );

    // Filter return reasons based on user's professional profiles (/person/my_types)
    const filteredReturnReasons = useMemo(() => {
        // Wait for both returnReasons AND userProfessionalTypes to be loaded
        if (returnReasons.length === 0 || userProfessionalTypes === null) return returnReasons;

        // Admin / management users have no professional types → show all
        if (userProfessionalTypes.length === 0) return returnReasons;

        const REASONS_BY_TYPE = {
            institution_responsible: [RETURN_REASON_KEYS.QUALITY_ISSUE, RETURN_REASON_KEYS.ORDER_DIVERGENCE],
            distributor_responsible: [RETURN_REASON_KEYS.QUALITY_ISSUE, RETURN_REASON_KEYS.ORDER_DIVERGENCE],
            pharmacist: [RETURN_REASON_KEYS.TREATMENT_DISCONTINUATION, RETURN_REASON_KEYS.QUALITY_ISSUE],
            doctor: [RETURN_REASON_KEYS.TREATMENT_DISCONTINUATION, RETURN_REASON_KEYS.QUALITY_ISSUE],
        };

        const allowedReasons = new Set(
            userProfessionalTypes.flatMap(type => REASONS_BY_TYPE[type] ?? [])
        );

        return returnReasons.filter(r => allowedReasons.has(r.name));
    }, [returnReasons, userProfessionalTypes]);

    return {
        state: {
            data,
            selectedReason,
            submitted,
            checkSubmitted,
            files,
            existingFiles,
            isLoading,
            batches,
            organizationBranches,
            returnReasons: filteredReturnReasons,
            dispensings,
            searchedDispensings,
            checkoutData,
            lastReturnReason,
            formIsValid,
            institutionOptions,
        },
        handlers: {
            setData,
            setSelectedReason,
            setSubmitted,
            setCheckSubmitted,
            setFiles,
            setSearchedDispensings,
            setCheckoutData,
            clearAllFields,
            handleChange,
            handleReturnReasonChange,
            handleInstitutionChange,
            handleFileSelect,
            handleCheckoutDataChange,
            handleCheckoutSubmitted,
            fieldHasError,
            helperText,
            handleSave,
        }
    };
}
